import { z } from 'zod';

/**
 * Canonical Place Details (Card v1) FieldMask — drives the Google Places (New)
 * API request sent by the `/api/places/[placeId]` proxy when callers pass
 * `?v=card-v1`. The FieldMask is **cost-bearing**: Google bills per field-class
 * returned, so any change here shifts the SKU tier applied to every call.
 *
 * Cost tiers (Places API (New) —
 * https://developers.google.com/maps/billing-and-pricing/pricing#places):
 *
 *   Essentials (free within cap)
 *     id, displayName, photos (metadata only), location
 *   Pro (billable)
 *     primaryTypeDisplayName, rating, userRatingCount, websiteUri,
 *     internationalPhoneNumber
 *   Enterprise (billable, higher tier)
 *     currentOpeningHours
 *
 * Mixing tiers bills at the highest tier touched by the mask.
 *
 * Google's FieldMask grammar does not support `photos[0]` indexing — the full
 * array is requested and sliced client-side.
 */
export const PLACE_DETAILS_CARD_V1_FIELD_MASK = [
  'id',
  'displayName',
  'primaryTypeDisplayName',
  'photos',
  'rating',
  'userRatingCount',
  'currentOpeningHours',
  'websiteUri',
  'internationalPhoneNumber',
  'location',
] as const;

export const PLACE_DETAILS_CARD_V1_FIELD_MASK_HEADER = PLACE_DETAILS_CARD_V1_FIELD_MASK.join(',');

/**
 * Basic / Essentials tier — billed at the Places (New) "Essentials" SKU.
 * These fields carry the lowest per-request charge and would be the full
 * shape of a future List/Carousel-only mask.
 */
export const PLACE_DETAILS_BASIC_FIELDS = ['displayName', 'photos'] as const;

/**
 * Popularity signals — billed at the Places (New) "Pro" SKU (same tier as
 * Contact below). Split into its own group because a future List/Carousel
 * view needs star rating + review count even though it can omit address and
 * phone. Keeping it separate documents the SKU boundary the card-v1 mask
 * already crosses.
 */
export const PLACE_DETAILS_POPULARITY_FIELDS = ['rating', 'userRatingCount'] as const;

/**
 * Contact fields — billed at the Places (New) "Pro" SKU. Requested because
 * `PlaceInfoPanel` renders address, tel link, and website CTA. Adding any
 * Popularity or Contact field escalates the whole mask from Essentials to
 * Pro; the SKU is charged once per request regardless of how many Pro-tier
 * fields are present.
 */
export const PLACE_DETAILS_CONTACT_FIELDS = [
  'formattedAddress',
  'nationalPhoneNumber',
  'websiteUri',
] as const;

/**
 * Atmosphere tier — billed at the Places (New) "Enterprise" SKU, the highest
 * tier touched by this mask. `currentOpeningHours` drives the open-now
 * status chip; `editorialSummary` renders below the address block. Dropping
 * both fields would downgrade the whole mask to Pro.
 */
export const PLACE_DETAILS_ATMOSPHERE_FIELDS = ['currentOpeningHours', 'editorialSummary'] as const;

/**
 * Legacy FieldMask — used when the proxy is called without `?v=card-v1`. Kept
 * so `PlaceInfoPanel`'s existing direct-fetch continues rendering address,
 * phone, and editorialSummary until it migrates to an explicit version flag.
 *
 * Cost tiers (Places API (New) —
 * https://developers.google.com/maps/billing-and-pricing/pricing#places):
 *
 *   Essentials (Basic)
 *     displayName, photos (metadata only)
 *   Pro (Popularity + Contact)
 *     rating, userRatingCount, formattedAddress, nationalPhoneNumber,
 *     websiteUri
 *   Enterprise (Atmosphere)
 *     currentOpeningHours, editorialSummary
 *
 * Google bills at the highest tier touched by the mask. The legacy panel
 * currently reads every field, so this mask ships at the Enterprise tier; a
 * future split should serve List/Carousel views with
 * `PLACE_DETAILS_BASIC_FIELDS` + `PLACE_DETAILS_POPULARITY_FIELDS` (Pro) and
 * defer the remaining Contact / Atmosphere fields to an expanded-detail
 * fetch.
 *
 * TODO(FRE-661): Split into LIST_MASK (Basic + Popularity) and DETAIL_MASK
 * (all four groups) once `PlaceCard`/`PlaceCardCarousel` land.
 */
export const PLACE_DETAILS_LEGACY_FIELD_MASK = [
  ...PLACE_DETAILS_BASIC_FIELDS,
  ...PLACE_DETAILS_POPULARITY_FIELDS,
  ...PLACE_DETAILS_CONTACT_FIELDS,
  ...PLACE_DETAILS_ATMOSPHERE_FIELDS,
] as const;

export const PLACE_DETAILS_LEGACY_FIELD_MASK_HEADER = PLACE_DETAILS_LEGACY_FIELD_MASK.join(',');

/** Google Place ID wire format accepted by the proxy and RTK Query endpoint. */
export const PLACE_ID_PATTERN = /^[A-Za-z0-9_-]{10,}$/;

/** Zod schema mirroring `PLACE_ID_PATTERN` for parse-based validation. */
export const placeIdSchema = z.string().regex(PLACE_ID_PATTERN);

/** Returns true when `value` matches the accepted Google Place ID wire format. */
export function isValidPlaceId(value: string): boolean {
  return PLACE_ID_PATTERN.test(value);
}

/**
 * Place Details version identifier sent as the `?v=` query token to the
 * `/api/places/[placeId]` proxy. Controls which FieldMask the proxy forwards
 * to Google and which normalization shape it returns.
 */
export const PLACE_DETAILS_VERSION = {
  CARD_V1: 'card-v1',
} as const;

/** Allowed `?v=` query-param values for place-details proxy responses. */
export type PlaceDetailsVersion =
  (typeof PLACE_DETAILS_VERSION)[keyof typeof PLACE_DETAILS_VERSION];

const localizedTextSchema = z.object({
  text: z.string().optional(),
  languageCode: z.string().optional(),
});

const photoSchema = z.object({
  name: z.string().optional(),
  widthPx: z.number().optional(),
  heightPx: z.number().optional(),
  authorAttributions: z
    .array(
      z.object({
        displayName: z.string().optional(),
        uri: z.string().optional(),
        photoUri: z.string().optional(),
      }),
    )
    .optional(),
});

const openingHoursSchema = z.object({
  openNow: z.boolean().optional(),
  weekdayDescriptions: z.array(z.string()).optional(),
});

const latLngSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

/**
 * Normalized response shape returned by the proxy to the client for the
 * Card v1 FieldMask. Fields are all optional because Google may omit any of
 * them on a given Place (rating is missing if there are no reviews, etc.).
 * `id` is required so the cache entry has a deterministic identifier.
 */
export const placeCardDetailsV1Schema = z.object({
  id: z.string().min(1),
  displayName: z.string().optional(),
  primaryTypeDisplayName: z.string().optional(),
  photoUrls: z.array(z.string()).optional(),
  rating: z.number().min(0).max(5).optional(),
  userRatingCount: z.number().int().nonnegative().optional(),
  currentOpeningHours: openingHoursSchema.optional(),
  websiteUri: z.string().optional(),
  internationalPhoneNumber: z.string().optional(),
  location: latLngSchema.optional(),
});

/**
 * Normalized card-v1 place-details payload returned to web clients. All
 * fields except `id` are optional because Google may omit any of them on a
 * given Place.
 */
export type PlaceCardDetailsV1 = z.infer<typeof placeCardDetailsV1Schema>;

/** Raw Google Places (New) response shape — the subset the proxy consumes. */
export const rawPlaceDetailsV1Schema = z.object({
  id: z.string().optional(),
  displayName: localizedTextSchema.optional(),
  primaryTypeDisplayName: localizedTextSchema.optional(),
  photos: z.array(photoSchema).optional(),
  rating: z.number().min(0).max(5).optional(),
  userRatingCount: z.number().int().nonnegative().optional(),
  currentOpeningHours: openingHoursSchema.optional(),
  websiteUri: z.string().optional(),
  internationalPhoneNumber: z.string().optional(),
  location: latLngSchema.optional(),
});

/**
 * Raw upstream Google Places (New) payload subset consumed by the proxy
 * normalizer before it is flattened into `PlaceCardDetailsV1`.
 */
export type RawPlaceDetailsV1 = z.infer<typeof rawPlaceDetailsV1Schema>;
